/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { conformJurisdictionalId } from 'linced-app/utils/fields';
import { conformAmount, formatAmount } from 'linced-app/utils/finance';
import {
  conformDate,
  isTruthy,
  toCamelCase,
} from '@linc-technologies/linced-common/utils/strings';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import Papa from 'papaparse';

export default class FinanceController extends Controller {
  @service
  session;

  @service
  store;

  @service
  tax;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @tracked
  currentStep = 0;

  @action
  setCurrentStep(value) {
    this.currentStep = value;
  }

  fileTypes = Object.freeze(['text/csv', '.csv']);

  @tracked
  file = null;

  @action
  setFile(value) {
    this.file = value;
  }

  @computed('jurisdiction')
  get requiredColumns() {
    let jurisdictionalIdLabel = 'Student ID';
    if (this.jurisdiction === Jurisdiction.NZ_MOE) {
      jurisdictionalIdLabel = 'NSN';
    }

    return [
      { key: 'jurisdictionalId', label: jurisdictionalIdLabel },
      { key: 'ledgerCode', label: 'Ledger Code' },
      { key: 'ledgerName', label: 'Ledger Name' },
      { key: 'ledgerItemName', label: 'Ledger Item Name' },
      { key: 'description', label: 'Line Item Description' },
      { key: 'date', label: 'Date' },
      { key: 'amount', label: 'Amount' },
      { key: 'isDonation', label: 'Is a Donation' },
    ];
  }

  @tracked
  fileHasCorrectColumns = false;

  @tracked
  columnCheckInProgress = true;

  @tracked
  columnMap = null;

  @tracked
  importProcessing = false;

  @tracked
  numLedgersCreated = 0;

  @tracked
  numLedgerItemsCreated = 0;

  @tracked
  numItemsCreated = 0;

  @tracked
  numRowsSkipped = 0;

  @tracked
  numLedgerErrors = 0;

  @tracked
  numItemErrors = 0;

  get numErrors() {
    return this.numLedgerErrors + this.numItemErrors;
  }

  @tracked
  numWarnings = 0;

  @tracked
  numRows = 0;

  @tracked
  missingJurisdictionalIds = null;

  @tracked
  seenHeader = false;

  @tracked
  ledgerMap = null;

  @tracked
  ledgerPromises = null;

  @tracked
  ledgerItemMap = null;

  @tracked
  ledgerItemPromises = null;

  @tracked
  lineItemPromises = null;

  @tracked
  errors;

  @tracked
  warnings;

  @computed('model.{accounts.[],learners.[]}')
  get accountMap() {
    return this.model.accounts.reduce((map, account) => {
      let jurisdictionalId = account.get('person.jurisdictionalId');
      if (!isEmpty(jurisdictionalId)) {
        map[jurisdictionalId] = account;
      }
      return map;
    }, {});
  }

  get importing() {
    if (this.importProcessing) {
      return true;
    }
    let numDone =
      this.numRowsSkipped + this.numItemsCreated + this.numItemErrors;
    return numDone !== this.numRows;
  }

  // conformLedgerItemKey conforms a Ledger Item name in order to dedupe names
  // with insignificant changes such as extra spaces or capitalisation
  // differences.  The first-seen name variant will be used when creating a new
  // Ledger Item.
  conformLedgerItemKey(name) {
    return toCamelCase(name.toLowerCase());
  }

  handleParsedColumns(results) {
    let headers = results.data[0];
    let map = {};
    let hasAllColumns = this.requiredColumns.every((col) => {
      let index = headers.indexOf(col.label);
      if (index === -1) {
        return false;
      }
      map[col.key] = index;
      return true;
    });

    this.fileHasCorrectColumns = hasAllColumns;
    this.columnCheckInProgress = false;
    this.columnMap = hasAllColumns ? map : {};
  }

  importRow(row) {
    // Skip header row.
    if (!this.seenHeader) {
      this.seenHeader = true;
      return;
    }

    // Skip empty rows.
    let data = row.data;
    if (isEmpty(data) || data.every((cell) => isEmpty(cell))) {
      return;
    }

    this.incrementProperty('numRows');

    let map = this.columnMap;

    // Check if we have a jurisdictional ID.
    let jurisdictionalId = data[map.jurisdictionalId].trim();
    jurisdictionalId = conformJurisdictionalId(
      this.jurisdiction,
      jurisdictionalId,
    );
    if (isEmpty(jurisdictionalId)) {
      this.errors = [...this.errors, { message: 'Missing student ID', data }];
      this.incrementProperty('numItemErrors');
      return;
    }

    // Check if we have an account in the map for this jurisdictional ID.
    let account = this.get(`accountMap.${jurisdictionalId}`);
    if (isEmpty(account)) {
      if (!this.missingJurisdictionalIds.includes(jurisdictionalId)) {
        this.missingJurisdictionalIds = [
          ...this.missingJurisdictionalIds,
          jurisdictionalId,
        ];
      }
      this.incrementProperty('numRowsSkipped');
      return;
    }

    // Handle ledger creation (where needed).
    let ledgerCode = data[map.ledgerCode].trim();
    let ledgerPromise = Promise.resolve();
    if (!isEmpty(ledgerCode)) {
      let ledgerName = data[map.ledgerName].trim();
      ledgerPromise = this.getLedgerPromise(ledgerCode, ledgerName);
    }
    let name = data[map.ledgerItemName].trim();
    if (isEmpty(name)) {
      name = 'Sundry Items';
    }
    let date = conformDate(data[map.date]);
    ledgerPromise
      .then((ledger) => {
        // Use the isDonation flag from the first-processed line item if we end
        // up creating a new Ledger Item.
        let isDonation = isTruthy(data[map.isDonation]);
        return this.getLedgerItemPromise(ledger, date, name, isDonation);
      })
      .then(async (ledgerItem) => {
        let isDonation = isTruthy(data[map.isDonation]);
        let amount = formatAmount(conformAmount(data[map.amount]));
        if (amount < 0.01) {
          // If the amount is zero or negative, log it as an error and move on.
          this.errors = [
            ...this.errors,
            { message: 'Amount must be at least $0.01', data },
          ];
          this.incrementProperty('numItemErrors');
          return Promise.resolve();
        }

        let item = this.store.createRecord('finance/line-item', {
          school: this.session.school,
          account,
          // A belongsTo takes a record, not the async proxy.
          ledger: await ledgerItem.ledger,
          ledgerItem,
          name,
          description: data[map.description],
          date,
          amount,
          isDonation,
          isTaxable: !isDonation,
        });
        if (!isDonation) {
          let taxType = ledgerItem.get('ledger.taxType');
          item.set('taxPercentage', this.tax.getRate(item.date, taxType) || 0);
        }

        let ledgerId = ledgerItem.get('ledger.id');
        if (isEmpty(this.lineItemPromises[ledgerId])) {
          // Start a fresh/empty chain for this Ledger.
          this.lineItemPromises[ledgerId] = Promise.resolve();
        }

        // Append the promise for this Line Item to the Ledger's chain.
        this.lineItemPromises[ledgerId] = this.lineItemPromises[ledgerId].then(
          () => {
            return item
              .save()
              .then(() => {
                this.incrementProperty('numItemsCreated');
              })
              .catch(() => {
                this.incrementProperty('numItemErrors');
                this.errors = [
                  ...this.errors,
                  { message: 'Failed to create Line Item', data },
                ];
              });
          },
        );
      });
  }

  getLedgerPromise(code, name) {
    let ledger = this.ledgerMap[code];
    if (isEmpty(ledger)) {
      let ledgerPromise = this.ledgerPromises[code];
      if (isEmpty(ledgerPromise)) {
        let ledger = this.store.createRecord('finance/ledger', {
          school: this.session.school,
          code,
          name,
        });
        ledgerPromise = ledger.save().then((record) => {
          this.incrementProperty('numLedgersCreated');
          this.ledgerMap[code] = record;
          return record;
        });
        this.ledgerPromises[code] = ledgerPromise;
      }
      return ledgerPromise;
    } else {
      return Promise.resolve(ledger);
    }
  }

  getLedgerItemPromise(ledger, date, name, isDonation) {
    let year = date.substring(0, 4);
    let nameKey = this.conformLedgerItemKey(name);
    let yearItems = this.ledgerItemMap[year];
    if (isEmpty(yearItems)) {
      this.ledgerItemMap[year] = {};
      yearItems = this.ledgerItemMap[year];
    }
    if (isEmpty(yearItems[ledger.id])) {
      yearItems[ledger.id] = {};
    }
    let ledgerItem = yearItems[ledger.id][nameKey];
    if (isEmpty(ledgerItem)) {
      let yearPromises = this.ledgerItemPromises[year];
      if (isEmpty(yearPromises)) {
        this.ledgerItemPromises[year] = {};
        yearPromises = this.ledgerItemPromises[year];
      }
      let itemPromises = yearPromises[ledger.id];
      if (isEmpty(itemPromises)) {
        this.ledgerItemPromises[year][ledger.id] = {};
        itemPromises = this.ledgerItemPromises[year][ledger.id];
      }
      if (isEmpty(itemPromises[nameKey])) {
        let item = this.store.createRecord('finance/ledger-item', {
          school: this.session.school,
          ledger,
          name,
          defaultAmount: 1,
          defaultDate: date,
          defaultDescription: '',
          isDonation,
          isTaxable: !isDonation,
        });
        this.ledgerItemPromises[year][ledger.id][nameKey] = item
          .save()
          .then((record) => {
            this.incrementProperty('numLedgerItemsCreated');
            this.ledgerItemMap[year][ledger.id][nameKey] = record;
            return record;
          });
      }
      return this.ledgerItemPromises[year][ledger.id][nameKey];
    } else {
      return Promise.resolve(ledgerItem);
    }
  }

  importComplete() {
    this.importProcessing = false;
  }

  @action
  checkColumns() {
    this.fileHasCorrectColumns = false;
    this.columnCheckInProgress = true;
    this.columnMap = {};
    Papa.parse(this.file.data, {
      header: false,
      preview: 1,
      complete: this.handleParsedColumns.bind(this),
    });
  }

  @action
  import() {
    this.importProcessing = true;
    this.numLedgersCreated = 0;
    this.numLedgerItemsCreated = 0;
    this.numItemsCreated = 0;
    this.numRowsSkipped = 0;
    this.numLedgerErrors = 0;
    this.numItemErrors = 0;
    this.numWarnings = 0;
    this.errors = [];
    this.warnings = [];
    this.missingJurisdictionalIds = [];
    this.seenHeader = false;
    this.ledgerMap = this.model.ledgers.reduce((map, ledger) => {
      map[ledger.code] = ledger;
      return map;
    }, {});
    this.ledgerPromises = {};
    this.ledgerItemMap = this.model.ledgerItems.reduce((map, ledgerItem) => {
      let year = ledgerItem.defaultDate.substring(0, 4);
      if (isEmpty(map[year])) {
        map[year] = {};
      }
      let ledgerId = ledgerItem.get('ledger.id');
      if (isEmpty(map[year][ledgerId])) {
        map[year][ledgerId] = {};
      }
      let nameKey = this.conformLedgerItemKey(ledgerItem.name.trim());
      map[year][ledgerId][nameKey] = ledgerItem;
      return map;
    }, {});
    this.ledgerItemPromises = {};
    this.lineItemPromises = {};

    Papa.parse(this.file.data, {
      header: false,
      step: this.importRow.bind(this),
      complete: this.importComplete.bind(this),
    });
  }

  reset() {
    this.currentStep = 0;
    this.file = null;
    this.fileHasCorrectColumns = false;
    this.columnCheckInProgress = true;
    this.columnMap = {};
    this.importProcessing = false;
    this.numLedgersCreated = 0;
    this.numLedgerItemsCreated = 0;
    this.numItemsCreated = 0;
    this.numRowsSkipped = 0;
    this.numLedgerErrors = 0;
    this.numItemErrors = 0;
    this.numWarnings = 0;
    this.errors = [];
    this.warnings = [];
    this.missingJurisdictionalIds = [];
    this.seenHeader = false;
    this.ledgerPromises = {};
    this.ledgerItemMap = {};
    this.ledgerItemPromises = {};
  }
}
